import type { ScaleTime } from 'd3-scale'
import { area, curveStepAfter } from 'd3-shape'
import { useMemo } from 'react'

/** Pattern stroke color - blue to match forecast styling */
const PATTERN_STROKE = '#6366f1' // Indigo-500

/** Pattern background color */
const PATTERN_BACKGROUND = 'transparent'

/** Pattern ID for reference */
export const ESTIMATION_PATTERN_ID = 'estimation-pattern'

type EstimationMarkersProps = {
  datetimes: Date[]
  estimated: boolean[]
  timeScale: ScaleTime<number, number>
  height: number
}

/**
 * SVG pattern definition for hatched/crosshatched estimation overlay.
 * Uses round cap dashes for a softer appearance.
 * Must be rendered inside an SVG <defs> block.
 */
export function EstimationPatternDef() {
  return (
    <pattern height="8" id={ESTIMATION_PATTERN_ID} patternUnits="userSpaceOnUse" viewBox="0 0 8 8" width="8">
      <rect fill={PATTERN_BACKGROUND} height="8" width="8" />
      {/* Diagonal dashed lines with round caps */}
      <path d="M-5 13L15 -7" stroke={PATTERN_STROKE} strokeDasharray="3 3" strokeLinecap="round" />
      <path d="M-1 17L19 -3" stroke={PATTERN_STROKE} strokeDasharray="3 3" strokeLinecap="round" />
      <path d="M-10 10L10 -10" stroke={PATTERN_STROKE} strokeDasharray="3 3" strokeLinecap="round" />
    </pattern>
  )
}

/**
 * Renders hatched overlay areas for estimated data points.
 * Uses curveStepAfter to create bar-like stepped regions.
 */
function EstimationMarkers({ datetimes, estimated, timeScale, height }: Readonly<EstimationMarkersProps>) {
  const pathData = useMemo(() => {
    if (datetimes.length === 0 || estimated.length === 0) return null

    // Extend datetimes with an extra point at the end to ensure the last bar extends fully
    // Calculate the interval from existing data points
    let endTime: Date
    if (datetimes.length >= 2) {
      const interval = datetimes[1].getTime() - datetimes[0].getTime()
      endTime = new Date(datetimes[datetimes.length - 1].getTime() + interval)
    } else {
      // Fallback: add 1 hour
      endTime = new Date(datetimes[datetimes.length - 1].getTime() + 3600000)
    }

    const extendedDatetimes = [...datetimes, endTime]
    const extendedEstimated = [...estimated, estimated[estimated.length - 1]]

    // Create area generator for estimated regions
    // y0 = 0 (bottom), y1 = -height when estimated (goes up from transform origin)
    const estimationArea = area<Date>()
      .curve(curveStepAfter)
      .x((d) => timeScale(d))
      .y0(0)
      .y1((d, index) => (extendedEstimated[index] ? -height : 0))

    return estimationArea(extendedDatetimes)
  }, [datetimes, estimated, timeScale, height])

  if (!pathData) return null

  return (
    <g style={{ pointerEvents: 'none' }} transform={`translate(0, ${height})`}>
      {/* Semi-transparent white background to wash out the chart behind */}
      <path d={pathData} fill="white" opacity={0.4} />
      {/* Hatched pattern overlay */}
      <path d={pathData} fill={`url(#${ESTIMATION_PATTERN_ID})`} opacity={0.5} />
    </g>
  )
}

export default EstimationMarkers
